import { define, reactive, toJS } from '@rehaje/reactive'
import { isArray, keys } from '@rehaje/shared'

import { createJSDoc } from '../lambdas/createJSDoc'

import type { Store } from '../stores/Store'
import type {
  InternalNode,
  ScopeTypes,
  ScopeVariableDeclarations,
  TypedSchema,
  TypedVariableDeclaration
} from '../types'
import type { TypedNode } from './TypedNode'
import type { ViewScope } from './ViewScope'
import type { TRecord } from '@rehaje/shared'

/**
 * 类型作用域模型，与 Typescript 的作用域推导类似，主要是用于构建静态作用域树
 * 辅助各种 code editor 进行局部作用域变量推导
 */
export class TypedScope implements InternalNode<TypedScope> {
  static __r_scope: TRecord = {}

  get __r_accessors() {
    const accessors: TRecord<(scope: ViewScope) => any> = {}
    for (const variable of this.variables) {
      accessors[variable.name] = (scope) =>
        this.__r_accessVariable(variable, scope)
    }
    return accessors
  }

  @reactive('computed')
  get __r_block_defs(): TypedVariable[] {
    return this.block === this ? this.__r_recursionMergeLocal() : []
  }

  @reactive('computed')
  get __r_defs() {
    if (!this.node.__r_scope_declaration) return []
    return this.__r_getScopeDeclarations(this.node.__r_scope_declaration.variables)
  }

  get __r_dependency_accessors() {
    const accessors: TRecord<(scope: ViewScope) => any> = {}
    for (const variable of this.dependencies) {
      accessors[variable.name] = (scope) =>
        this.__r_accessVariable(variable, scope)
    }
    return accessors
  }

  get __r_environments_defs(): TypedVariable[] {
    return this.store.plugin.environments
  }

  @reactive('computed')
  get __r_local_defs(): TypedVariable[] {
    const variables: TypedVariable[] = []
    for (const declaration of this.__r_defs) {
      if (declaration.type !== 'Local') continue
      variables.push(new TypedVariable(this.node, declaration))
    }
    return variables
  }

  get __r_parent_defs(): TypedVariable[] {
    return this.root === this ? [] : (this.parent?.variables ?? [])
  }

  @reactive('computed')
  get __r_root_defs(): TypedVariable[] {
    if (this !== this.root) return this.root.__r_root_defs
    const variables: TypedVariable[] = []
    for (const key of keys(this.store.__r_variables ?? {})) {
      const definition = this.store.definition
      const variable = this.store.__r_variables?.[key]
      variables.push(
        new TypedVariable(this.node, {
          name: key,
          get schema() {
            return definition.inferShapeSchema(variable)
          },
          value: variable
        })
      )
    }
    return variables
  }

  @reactive('computed')
  get __r_through_defs(): TypedVariable[] {
    const variables: TypedVariable[] = []
    for (const declaration of this.__r_defs) {
      if (declaration.type !== 'Block') continue
      variables.push(new TypedVariable(this.node, declaration))
    }
    return variables
  }

  /**
   * 最近的块级作用域
   */
  get block(): TypedScope {
    if (this.type === 'Block') return this
    return this.parent?.block ?? /* v8 ignore next */ this
  }

  /**
   * 子节点集合
   */
  @reactive('computed')
  get children(): TypedScope[] {
    return this.node.__r_children.map((node) => node.__r_typed_scope)
  }

  @reactive('computed', {
    equals: isEqualVariables
  })
  get dependencies(): TypedVariable[] {
    const dependencies = this.node.getDependencyVariables()
    if (dependencies.length === 0) return []
    return this.variables.filter((variable) =>
      dependencies.includes(variable.name)
    )
  }

  /**
   * 子孙集合
   */
  @reactive('computed')
  get descendants(): TypedScope[] {
    return this.children.flatMap((node) => [node, ...node.descendants])
  }

  /**
   * 全局作用域节点
   */
  get global() {
    return this.root
  }

  /**
   * 父节点
   */
  get parent(): TypedScope | undefined {
    return this.node.__r_parent?.__r_typed_scope
  }

  /**
   * 父节点集合
   */
  @reactive('computed')
  get parents(): TypedScope[] {
    if (this.parent) {
      return [this.parent, ...this.parent.parents]
    }
    return []
  }

  /**
   * 根节点
   */
  get root(): TypedScope {
    return this.node.__r_root.__r_typed_scope
  }

  /**
   * 状态中心
   */
  get store(): Store {
    return this.node.__r_store
  }

  /**
   * 作用域类型
   */
  get type(): ScopeTypes {
    const node = this.node
    if (node.__r_parent === undefined) return 'Block'
    return node.__r_scope_declaration?.type ?? 'Local'
  }

  /**
   * 作用域变量 TypeScript 声明
   */
  @reactive('computed')
  get typescript() {
    const variables = this.variables
    const typescript = variables
      .map((variable) => variable.typescript)
      .join('\n')
    return typescript
  }

  /**
   * 变量集合
   */
  @reactive('computed', {
    equals: isEqualVariables
  })
  get variables(): TypedVariable[] {
    if (this.root === this) {
      return this.__r_mergeDeclarations(
        this.__r_local_defs,
        this.__r_block_defs,
        this.__r_environments_defs,
        this.__r_root_defs
      )
    } else if (this.block === this) {
      return this.__r_mergeDeclarations(
        this.__r_local_defs,
        this.__r_block_defs,
        this.__r_parent_defs
      )
    } else {
      return this.__r_mergeDeclarations(this.__r_local_defs, this.__r_parent_defs)
    }
  }

  constructor(public node: TypedNode) {
    define(this)
  }

  __r_accessVariable(variable: TypedVariable, scope: ViewScope) {
    const node = variable.from
    if (!variable.get) return variable.value
    const target = node?.__r_getSelfNode(scope.node.__r_indexes)
    return variable.get.call(target ?? node)
  }

  __r_getScopeDeclarations(
    declarations: ScopeVariableDeclarations[] = []
  ): TypedVariableDeclaration[] {
    const variables: TypedVariableDeclaration[] = []
    for (const declaration of declarations) {
      if (isArray(declaration)) {
        variables.push(...declaration)
      } else if (typeof declaration === 'function') {
        variables.push(...declaration.call(this.node))
      }
    }
    return variables
  }

  __r_mergeDeclarationReverse(
    visited: string[],
    target: TypedVariable[]
  ): TypedVariable[] {
    const result: TypedVariable[] = []
    for (let i = target.length - 1; i >= 0; i--) {
      const variable = target[i]
      if (visited.includes(variable.name)) continue
      visited.push(variable.name)
      result.push(variable)
    }
    return result.toReversed()
  }

  __r_mergeDeclarations(
    target: TypedVariable[],
    ...sources: TypedVariable[][]
  ): TypedVariable[] {
    sources = sources.filter((source) => source.length > 0)
    if (sources.length === 0) return target
    else if (target.length === 0) {
      return this.__r_mergeDeclarations(sources[0], ...sources.slice(1))
    } else if (target.length === 1 && sources.length === 1) {
      const name = target[0].name // self
      return [target[0], ...sources[0].filter((source) => source.name !== name)]
    }

    // 层间从上到下合并去重，层内从后往前合并去重，但结果相对顺序不变
    const result: TypedVariable[] = []
    const visited: string[] = []
    result.push(...this.__r_mergeDeclarationReverse(visited, target))
    for (const source of sources) {
      result.push(...this.__r_mergeDeclarationReverse(visited, source))
    }
    return result
  }

  __r_recursionMergeLocal() {
    const variables: TypedVariable[][] = []
    for (const child of this.children) {
      if (child.type === 'Block') continue
      variables.push(child.__r_recursionMergeLocal())
    }
    return this.__r_mergeDeclarations([], ...variables, this.__r_through_defs)
  }

  /**
   * 获取子节点集合
   */
  getChildren(): TypedScope[] {
    return this.children
  }

  /**
   * 获取子孙集合
   */
  getDescendants(): TypedScope[] {
    return this.descendants
  }

  /**
   * 获取父节点
   */
  getParent(): TypedScope | undefined {
    return this.parent
  }

  /**
   * 获取父节点集合
   */
  getParents(): TypedScope[] {
    return this.parents
  }

  /**
   * 获取根节点
   */
  getRoot(): TypedScope {
    return this.root
  }

  /**
   * 获取状态中心
   */
  getStore(): Store {
    return this.store
  }

  /**
   * 获取当前作用域所有变量类型声明
   * @returns
   */
  getTypeScript() {
    return this.variables.map((variable) => variable.typescript).join('\n')
  }

  toJS() {
    return {
      type: this.type,
      variables: toJS(this.variables)
    }
  }
}

export class TypedVariable {
  __r_from: (() => TypedNode) | TypedNode
  __r_schema: TypedVariableDeclaration['schema']

  /**
   * 变量描述
   */
  description?: string
  /**
   * 额外信息
   */
  extra?: TRecord
  /**
   * 变量取值函数
   */
  get?: (this: TypedNode) => any
  /**
   * 变量分组
   */
  group?: string
  /**
   * 变量名
   */
  name: string
  /**
   * 变量标题
   */
  title?: string
  /**
   * 变量作用域类型
   */
  type?: ScopeTypes
  /**
   * 变量值
   */
  value?: any

  get __r_definition() {
    return this.from.__r_store.definition
  }

  get from(): TypedNode {
    const from = this.__r_from
    if (typeof from === 'function') return from()
    return from
  }

  /**
   * 变量 Schema
   */
  @reactive('computed')
  get schema(): TypedSchema {
    return this.__r_definition.__r_injectNode(() => {
      if (typeof this.__r_schema === 'function')
        return this.__r_schema.call(this.from)
      if (typeof this.__r_schema?.type === 'string') return this.__r_schema
      return this.__r_definition.infer(
        typeof this.get === 'function' ? this.get.call(this.from) : this.value
      )
    }, this.from)
  }

  /**
   * 作用域变量 TypeScript 声明
   */
  @reactive('computed')
  get typescript(): string {
    return `
${createJSDoc(this.schema, this.title, this.description)}
declare var ${this.name}: ${this.__r_definition.inferTypeScript(this.schema)}
`
  }

  constructor(
    from: (() => TypedNode) | TypedNode,
    source: TypedVariableDeclaration
  ) {
    this.__r_from = from
    this.__r_schema = source.schema
    this.description = source.description
    this.extra = source.extra
    this.get = source.get
    this.group = source.group
    this.title = source.title
    this.type = source.type
    this.value = source.value
    this.name = this.__r_createName(source.name)
    define(this)
  }

  __r_createName(name: TypedVariableDeclaration['name']): string {
    if (typeof name === 'function') return name.call(this.from) ?? ''
    return name ?? ''
  }
}

function isEqualVariables(newVars: TypedVariable[], oldVars: TypedVariable[]) {
  if (newVars.length !== oldVars.length) return false
  for (const [i, newVar] of newVars.entries()) {
    const oldVar = oldVars[i]
    if (newVar.name !== oldVar.name || newVar.from !== oldVar.from) return false
  }
  return true
}
